import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../api'
import { formatWhen, t } from '../i18n'
import { haptic, notify, openTelegram, useBackButton } from '../telegram'
import type { Coach, CoachFormat, CoachListItem, Game, PlayerMe } from '../types'

/** Языки — названиями на самих языках: так их ищут люди */
export const LANG_NAMES: Record<string, string> = {
  ru: 'Русский',
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
  pt: 'Português',
  uk: 'Українська',
}

export function coachPrice(coach: Coach, format: CoachFormat): number | null {
  return format === 'single' ? coach.price_single : format === 'split' ? coach.price_split : coach.price_group
}

/** Самая низкая из цен, которые тренер указал, — для карточки «от N €».
 *  Ровно те числа, что на его странице: без пересчёта «на человека». */
export function minPrice(coach: Coach): number | null {
  const prices = [coach.price_single, coach.price_split, coach.price_group].filter((v): v is number => v != null)
  return prices.length ? Math.min(...prices) : null
}

function euro(value: number): string {
  return `${String(value).replace('.', ',')} €`
}

export function CoachPhoto({ coach, size = 56 }: { coach: Coach; size?: number }) {
  const style = { width: size, height: size, fontSize: Math.round(size * 0.4) }
  return coach.photo_url ? (
    <img className="coach-photo" style={style} src={coach.photo_url} alt="" />
  ) : (
    <span className="coach-photo" style={style}>
      {coach.display_name.trim().slice(0, 1).toUpperCase() || '?'}
    </span>
  )
}

/** Карточка тренера в ленте на главной */
export function CoachTile({ coach, onOpen }: { coach: CoachListItem; onOpen: () => void }) {
  const from = minPrice(coach)
  const cities = Array.from(new Set(coach.clubs.map((c) => c.city)))
  return (
    <button className="coach-tile" onClick={onOpen}>
      <span className="row" style={{ gap: 10, alignItems: 'center' }}>
        <CoachPhoto coach={coach} size={48} />
        <span style={{ minWidth: 0 }}>
          <b className="ellipsis">{coach.display_name}</b>
          <span className="muted small ellipsis">{cities.join(' · ')}</span>
        </span>
      </span>
      <span className="muted small">
        {t().coachLevels(coach.level_min.toFixed(1), coach.level_max.toFixed(1))}
      </span>
      <span className="row between" style={{ marginTop: 'auto' }}>
        <span className="coach-langs">{coach.languages.map((l) => l.toUpperCase()).join(' · ')}</span>
        {from != null && <b>{t().coachFrom(from)}</b>}
      </span>
    </button>
  )
}

interface Props {
  me: PlayerMe
  /** Групповые тренировки из ленты: показываем их на странице тренера */
  trainings: Game[]
  /** Открыть сразу страницу тренера; «Назад» закрывает раздел (экран игры) */
  initialCoachId?: number | null
  /** Открыть страницу тренера из списка; «Назад» возвращает к списку (лента) */
  openFirst?: number | null
  onClose: () => void
  onOpenGame: (gameId: number) => void
}

/** Раздел «Тренеры»: список с фильтрами, страница тренера и заявка. */
export default function Coaches({ me, trainings, initialCoachId = null, openFirst = null, onClose, onOpenGame }: Props) {
  const [coaches, setCoaches] = useState<CoachListItem[] | null>(null)
  const [openId, setOpenId] = useState<number | null>(initialCoachId ?? openFirst)
  const [city, setCity] = useState<string | null>(null)
  const [lang, setLang] = useState<string | null>(null)

  useEffect(() => useBackButton(openId !== null && initialCoachId === null ? () => setOpenId(null) : onClose), [openId, initialCoachId, onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setCoaches(await api.coaches())
    } catch (e) {
      setCoaches([])
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const cities = useMemo(
    () => Array.from(new Set((coaches ?? []).flatMap((c) => c.clubs.map((club) => club.city)))).sort(),
    [coaches],
  )
  const langs = useMemo(() => Array.from(new Set((coaches ?? []).flatMap((c) => c.languages))), [coaches])
  const shown = (coaches ?? []).filter(
    (c) => (!city || c.clubs.some((club) => club.city === city)) && (!lang || c.languages.includes(lang)),
  )
  const open = coaches?.find((c) => c.id === openId) ?? null

  const back = open && initialCoachId === null ? () => setOpenId(null) : onClose

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().coachesTitle}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={back} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">🎓 {open ? open.display_name : t().coachesTitle}</div>
      </div>
      <div className="chat-list">
        {coaches === null && <p className="muted">{t().loading}</p>}
        {coaches && !open && (
          <>
            {cities.length > 1 && (
              <div className="chips" style={{ marginBottom: 8 }}>
                <button className="chip" aria-pressed={city === null} onClick={() => setCity(null)}>
                  {t().homeAllTowns}
                </button>
                {cities.map((c) => (
                  <button key={c} className="chip" aria-pressed={city === c} onClick={() => setCity(c)}>
                    {c}
                  </button>
                ))}
              </div>
            )}
            {langs.length > 1 && (
              <div className="chips" style={{ marginBottom: 12 }}>
                <button className="chip" aria-pressed={lang === null} onClick={() => setLang(null)}>
                  {t().coachesAnyLang}
                </button>
                {langs.map((l) => (
                  <button key={l} className="chip" aria-pressed={lang === l} onClick={() => setLang(l)}>
                    {LANG_NAMES[l] ?? l.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            {shown.length === 0 && <div className="card muted">{t().coachesEmpty}</div>}
            {shown.map((coach) => (
              <CoachRow key={coach.id} coach={coach} onOpen={() => setOpenId(coach.id)} />
            ))}
          </>
        )}
        {open && (
          <CoachPage
            coach={open}
            me={me}
            trainings={trainings.filter((g) => g.kind === 'training' && g.captain.id === open.player_id)}
            onOpenGame={onOpenGame}
            onChanged={load}
          />
        )}
      </div>
    </div>
  )
}

function CoachRow({ coach, onOpen }: { coach: CoachListItem; onOpen: () => void }) {
  const from = minPrice(coach)
  return (
    <button className="card coach-row" onClick={onOpen}>
      <CoachPhoto coach={coach} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="row between" style={{ gap: 8 }}>
          <b className="ellipsis">{coach.display_name}</b>
          {from != null && <b style={{ flex: '0 0 auto' }}>{t().coachFrom(from)}</b>}
        </span>
        {coach.certificate && <span className="muted small ellipsis">{coach.certificate}</span>}
        <span className="muted small ellipsis">{Array.from(new Set(coach.clubs.map((c) => c.city))).join(' · ')}</span>
        <span className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {coach.formats.map((f) => (
            <span key={f} className="badge neutral">
              {t().fmtName[f]}
            </span>
          ))}
          <span className="coach-langs">{coach.languages.map((l) => l.toUpperCase()).join(' · ')}</span>
        </span>
        {coach.my_request && (
          <span className={`coach-req ${coach.my_request.status}`}>{t().coachReq[coach.my_request.status]}</span>
        )}
      </span>
    </button>
  )
}

function CoachPage({
  coach,
  me,
  trainings,
  onOpenGame,
  onChanged,
}: {
  coach: CoachListItem
  me: PlayerMe
  trainings: Game[]
  onOpenGame: (id: number) => void
  onChanged: () => Promise<void>
}) {
  const own = coach.player_id === me.id
  const request = coach.my_request
  const [formOpen, setFormOpen] = useState(!request || request.status === 'declined')
  const [format, setFormat] = useState<CoachFormat>(coach.formats[0])
  const [when, setWhen] = useState<string[]>([])
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setFormOpen(!request || request.status === 'declined')
  }, [request])

  async function send() {
    setBusy(true)
    haptic('medium')
    try {
      await api.sendCoachRequest(coach.id, {
        format,
        when_text: when.length ? when.join(', ') : null,
        message: message.trim() || null,
      })
      notify(t().coachRequestSent)
      setMessage('')
      setWhen([])
      await onChanged()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="coach-hero">
        <CoachPhoto coach={coach} size={84} />
        <div style={{ minWidth: 0 }}>
          <div className="big">{coach.display_name}</div>
          {coach.certificate && <div className="muted">{coach.certificate}</div>}
          {coach.experience_years != null && <div className="muted small">{t().coachYears(coach.experience_years)}</div>}
        </div>
      </div>

      <div className="card">
        {coach.formats.map((f) => {
          const price = coachPrice(coach, f)
          return (
            <div key={f} className="row between price-row">
              <span>{t().fmtName[f]}</span>
              <span>
                {price != null ? <b>{euro(price)}</b> : '—'} <span className="muted small">{price != null ? t().fmtUnit[f] : ''}</span>
              </span>
            </div>
          )
        })}
        <div className="muted small" style={{ marginTop: 8 }}>
          {t().coachLevels(coach.level_min.toFixed(1), coach.level_max.toFixed(1))}
        </div>
      </div>

      <div className="card">
        <div style={{ whiteSpace: 'pre-line' }}>{coach.bio}</div>
        <div className="row between" style={{ marginTop: 12 }}>
          <span className="muted">{t().coachLanguages}</span>
          <span style={{ textAlign: 'right' }}>{coach.languages.map((l) => LANG_NAMES[l] ?? l).join(', ')}</span>
        </div>
        <div className="muted" style={{ marginTop: 10 }}>
          {t().coachClubs}
        </div>
        {coach.clubs.map((c) => (
          <div key={c.id}>
            📍 {c.name} · <span className="muted">{c.city}</span>
          </div>
        ))}
      </div>

      {trainings.length > 0 && (
        <div className="card">
          <div className="title" style={{ marginBottom: 6 }}>
            {t().coachUpcoming}
          </div>
          {trainings.map((g) => (
            <button key={g.id} className="admin-head" onClick={() => onOpenGame(g.id)}>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <b style={{ display: 'block' }}>{formatWhen(g.starts_at)}</b>
                <span className="muted small">
                  {g.club.name} · {t().level} {g.level_min.toFixed(1)}–{g.level_max.toFixed(1)}
                </span>
              </span>
              <span className="badge">{t().homeSeats(g.joined_count, g.max_players)}</span>
            </button>
          ))}
        </div>
      )}

      {!own && request && (
        <div className={`card coach-status ${request.status}`}>
          <div className="title">{t().coachReq[request.status]}</div>
          <div className="muted small">
            {t().fmtName[request.format]} · {formatWhen(request.created_at)}
          </div>
          {request.status === 'accepted' && (
            <div className="row" style={{ gap: 8, marginTop: 10 }}>
              {request.coach_link && (
                <button style={{ flex: 1 }} onClick={() => openTelegram(request.coach_link!)}>
                  {t().coachWrite}
                </button>
              )}
              {!formOpen && (
                <button className="ghost" style={{ flex: 1 }} onClick={() => setFormOpen(true)}>
                  {t().coachRequestAgain}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {!own && formOpen && (
        <div className="card">
          <div className="title">{t().coachRequestTitle}</div>
          <label>{t().coachRequestFormat}</label>
          <div className="chips">
            {coach.formats.map((f) => (
              <button key={f} className="chip" aria-pressed={format === f} onClick={() => setFormat(f)}>
                {t().fmtName[f]}
              </button>
            ))}
          </div>
          <label>{t().coachRequestWhen}</label>
          <div className="chips">
            {t().whenChips.map((w) => (
              <button
                key={w}
                className="chip"
                aria-pressed={when.includes(w)}
                onClick={() => setWhen((list) => (list.includes(w) ? list.filter((x) => x !== w) : [...list, w]))}
              >
                {w}
              </button>
            ))}
          </div>
          <label>{t().coachRequestMessage}</label>
          <textarea
            rows={3}
            maxLength={500}
            value={message}
            placeholder={t().coachRequestPlaceholder}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="muted small" style={{ marginTop: 8 }}>
            {t().coachRequestConsent}
          </div>
          <button style={{ width: '100%', marginTop: 12 }} disabled={busy} onClick={send}>
            {t().coachRequestSend}
          </button>
        </div>
      )}
    </div>
  )
}
